/**
 * Documentação do sistema — escrita para quem vai usar, não para quem
 * desenvolveu. Explica o processo de negócio, cada tela, de onde vem cada
 * número e o que fazer quando algo não bate.
 *
 * Os exemplos usam dados reais do primeiro demonstrativo processado, para que
 * o leitor reconheça os números que vê no sistema.
 */
import { PageHeader } from '../components/common';
import Ecossistema from '../components/Ecossistema';

const SECOES = [
  ['o-que-e', 'O que o FluxFin faz'],
  ['vocabulario', 'Vocabulário do dia a dia'],
  ['fluxo', 'O caminho de um recebimento'],
  ['lancar', 'Como lançar um recebimento'],
  ['conferencias', 'As conferências aritméticas'],
  ['de-onde-vem', 'De onde vem cada número'],
  ['tela-protocolo', 'Lendo: Baixa por protocolo'],
  ['tela-guias', 'Lendo: Por guia'],
  ['tela-arquivos', 'Lendo: Arquivos'],
  ['tela-pendencias', 'Lendo: Pendências'],
  ['tela-divergencias', 'Lendo: Divergências'],
  ['tela-glosas', 'Lendo: Glosas e auditoria'],
  ['tela-saldo', 'Lendo: Saldo a receber'],
  ['tela-agenda', 'Lendo: Agenda de envios'],
  ['modelos', 'Modelos de arquivo'],
  ['problemas', 'Quando algo não bate'],
  ['limites', 'O que o sistema não faz'],
  ['perguntas', 'Perguntas frequentes'],
];

function Secao({ id, num, titulo, children }) {
  return (
    <section className="ff-doc__secao" id={id}>
      <h2>
        <span className="ff-doc__num">{String(num).padStart(2, '0')}</span>
        {titulo}
      </h2>
      {children}
    </section>
  );
}

function Exemplo({ titulo = 'Exemplo real', children }) {
  return (
    <div className="ff-exemplo">
      <span className="ff-exemplo__rotulo">{titulo}</span>
      {children}
    </div>
  );
}

function Passos({ itens }) {
  return (
    <ol className="ff-passos">
      {itens.map((p, i) => (
        <li key={i}>
          <div>
            <strong>{p.titulo}</strong>
            <span>{p.texto}</span>
          </div>
        </li>
      ))}
    </ol>
  );
}

function TabelaRef({ colunas, linhas }) {
  return (
    <div className="ff-doc-tabela">
      <table>
        <thead>
          <tr>
            {colunas.map((c) => (
              <th key={c}>{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {linhas.map((l, i) => (
            <tr key={i}>
              {l.map((c, j) => (
                <td key={j}>{c}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function Documentacao() {
  return (
    <>
      <PageHeader
        titulo="Documentação"
        subtitulo="Como o FluxFin funciona, o que cada número significa e o que fazer quando algo não bate."
      />

      <div className="ff-doc">
        <nav className="ff-doc__indice" aria-label="Índice da documentação">
          <h2>Nesta página</h2>
          {SECOES.map(([id, titulo], i) => (
            <a key={id} href={`#${id}`}>
              <span>{String(i + 1).padStart(2, '0')}</span>
              {titulo}
            </a>
          ))}
        </nav>

        <div className="ff-doc__corpo">
          {/* ── 01 ────────────────────────────────────────────────────── */}
          <Secao id="o-que-e" num={1} titulo="O que o FluxFin faz">
            <p className="ff-doc__lide">
              O FluxFin responde a uma pergunta que hoje custa horas de trabalho manual:{' '}
              <strong>quanto a operadora pagou de cada protocolo?</strong>
            </p>

            <h3>O ecossistema, de uma olhada</h3>
            <p>
              Cada caixa abaixo é uma tela, e as setas são a ordem em que as coisas
              acontecem. Vale percorrer antes de ler o resto: quase toda dúvida sobre
              "de onde veio esse número" se responde seguindo as setas de trás para a
              frente.
            </p>
            <Ecossistema />

            <h3>O problema</h3>
            <p>
              O laboratório envia cobranças às operadoras agrupadas em <strong>protocolos</strong>.
              A operadora não paga o protocolo inteiro de uma vez — ela paga uma parte, glosa
              outra, e deixa saldo para pagamentos futuros em datas imprevisíveis.
            </p>
            <p>
              O retorno vem num <strong>demonstrativo de pagamento</strong>, que lista o valor
              pago <em>guia por guia</em> — e não menciona o protocolo em lugar nenhum. Já o
              financeiro, no Nasajon, controla o título <em>por protocolo</em>. Falta a ponte
              entre os dois, e é isso que alguém precisava montar à mão, guia por guia.
            </p>

            <h3>O que o sistema faz</h3>
            <p>
              O FluxFin lê o demonstrativo, extrai cada guia e o valor pago, consulta o Bioslab
              para descobrir a qual protocolo cada guia pertence, e soma. O resultado é uma lista
              curta: quanto baixar em cada protocolo.
            </p>

            <Exemplo titulo="Do arquivo ao número final">
              <p>
                Um demonstrativo da Unimed com 357 páginas e R$ 92.343,64 foi processado em
                segundos. Dele saíram <strong>7.978 linhas de procedimento</strong> agrupadas em{' '}
                <strong>899 guias</strong>, que o Bioslab resolveu para apenas{' '}
                <strong>2 protocolos</strong>:
              </p>
              <code className="ff-conta">
                {`476.03.2026        597 guias     R$ 62.737,64
476.04.2026 NIG 2  301 guias     R$ 29.355,20
                               ─────────────
                   898 guias     R$ 92.092,84`}
              </code>
            </Exemplo>
          </Secao>

          {/* ── 02 ────────────────────────────────────────────────────── */}
          <Secao id="vocabulario" num={2} titulo="Vocabulário do dia a dia">
            <p>
              Alguns termos aparecem em toda tela. Vale fixar o que cada um significa{' '}
              <em>dentro do FluxFin</em>.
            </p>

            <TabelaRef
              colunas={['Termo', 'O que é']}
              linhas={[
                [
                  <strong>Guia</strong>,
                  'O documento de cobrança de um atendimento — um paciente, um pedido de exames. É a chave que liga o arquivo da operadora ao Bioslab. Tem 11 dígitos.',
                ],
                [
                  <strong>Protocolo</strong>,
                  'O agrupamento de faturas que foi enviado à operadora para cobrança. Também chamado de processo. É a unidade em que a baixa acontece no Nasajon. Formato: 476.03.2026.',
                ],
                [
                  <strong>Operadora</strong>,
                  'O convênio que paga — Unimed, GEAP, Sulamérica. Cada uma manda o demonstrativo num formato diferente.',
                ],
                [
                  <strong>Competência</strong>,
                  'O mês em que a operadora efetuou o pagamento — é o que ela informa no cabeçalho do demonstrativo. Não tem relação com o mês do atendimento: um único pagamento costuma cobrir atendimentos de vários meses.',
                ],
                [
                  <strong>Glosa</strong>,
                  'Recusa total ou parcial do pagamento pela operadora, por divergência contratual ou técnica.',
                ],
                [
                  <strong>Baixa</strong>,
                  'A liquidação do título no financeiro. Parcial quando o valor recebido é menor que o total em aberto.',
                ],
                [
                  <strong>Valor bruto</strong>,
                  'A soma do que a operadora pagou por guia, antes dos impostos retidos e de descontos gerais. É este o valor da baixa.',
                ],
                [
                  <strong>Valor líquido</strong>,
                  'O que efetivamente caiu na conta, já com impostos retidos e descontos. Sempre menor que o bruto.',
                ],
              ]}
            />

            <h3>Três datas diferentes, e por que isso importa</h3>
            <p>
              A confusão mais comum no dia a dia é achar que um demonstrativo de maio contém
              atendimentos de maio. Não contém: a competência diz <strong>quando a operadora
              pagou</strong>, não quando o exame foi feito. São três datas distintas, e elas
              quase nunca coincidem:
            </p>
            <div className="ff-fluxo">
              <span className="ff-fluxo__passo">Atendimento — fev/2026</span>
              <span className="ff-fluxo__seta">→</span>
              <span className="ff-fluxo__passo">Protocolo — mar/2026</span>
              <span className="ff-fluxo__seta">→</span>
              <span className="ff-fluxo__passo destaque">Competência do pagamento — mai/2026</span>
            </div>
            <p>
              Por isso um único arquivo costuma conter guias de <strong>vários protocolos</strong>
              . No demonstrativo de exemplo havia atendimentos de fevereiro, março e abril
              convivendo no mesmo documento.
            </p>
          </Secao>

          {/* ── 03 ────────────────────────────────────────────────────── */}
          <Secao id="fluxo" num={3} titulo="O caminho de um recebimento">
            <p>
              Todo arquivo percorre os mesmos estados. Saber em qual estado ele está diz o que
              falta fazer.
            </p>

            <div className="ff-fluxo">
              <span className="ff-fluxo__passo">Recebido</span>
              <span className="ff-fluxo__seta">→</span>
              <span className="ff-fluxo__passo">Em conferência</span>
              <span className="ff-fluxo__seta">→</span>
              <span className="ff-fluxo__passo destaque">Confirmado</span>
              <span className="ff-fluxo__seta">→</span>
              <span className="ff-fluxo__passo">Protocolos resolvidos</span>
            </div>

            <TabelaRef
              colunas={['Estado', 'O que significa', 'O que fazer']}
              linhas={[
                [
                  <strong>Em conferência</strong>,
                  'O arquivo foi lido e as contas fecharam com o próprio documento. Ainda não alimenta a conciliação.',
                  'Abrir o arquivo e clicar em Confirmar revisão.',
                ],
                [
                  <strong>Confirmado</strong>,
                  'Passou pela revisão humana. Os valores já contam para a conciliação.',
                  'Clicar em Resolver protocolos no Bioslab.',
                ],
                [
                  <strong>Reprovado</strong>,
                  'As contas não fecharam. Os dados não foram gravados e não entram na conciliação.',
                  'Avisar um administrador — veja a seção 12.',
                ],
              ]}
            />

            <p>
              A separação entre <strong>confirmado</strong> e <strong>resolvido</strong> é
              proposital. Confirmar é dizer "os valores deste arquivo estão certos". Resolver é
              consultar o Bioslab para descobrir os protocolos. São passos diferentes porque o
              Bioslab pode estar indisponível — e isso não pode invalidar um arquivo já
              conferido.
            </p>
          </Secao>

          {/* ── 04 ────────────────────────────────────────────────────── */}
          <Secao id="lancar" num={4} titulo="Como lançar um recebimento">
            <p>
              É o processo que você mais vai repetir. Dá para enviar <strong>um arquivo ou
              vários de uma vez</strong> — a Unimed costuma mandar de 6 a 8 PDFs para a mesma
              referência de pagamento, e eles podem ir todos juntos.
            </p>

            <Passos
              itens={[
                {
                  titulo: 'Abra "Lançar recebimento"',
                  texto: 'No menu lateral, na seção Lançamento.',
                },
                {
                  titulo: 'Escolha a operadora primeiro',
                  texto:
                    'Só aparecem operadoras que já têm um modelo de leitura aprovado. Se a que você procura não está na lista, é porque o modelo dela ainda não foi cadastrado — fale com um administrador. Todos os arquivos do envio são desta mesma operadora.',
                },
                {
                  titulo: 'Selecione os arquivos',
                  texto:
                    'Pode escolher vários de uma vez, ou ir acrescentando. Cada um vira uma linha na tabela, e você pode remover os que entraram por engano. Até 30 por envio.',
                },
                {
                  titulo: 'Confira a competência de cada arquivo',
                  texto:
                    'O mês em que a operadora pagou, no formato 05/2026 — é a competência impressa no cabeçalho do demonstrativo, não o mês dos atendimentos. Cada arquivo tem a sua, porque um envio pode cobrir mais de uma competência. Para o caso comum, preencha o campo do topo e clique em "Aplicar a todos".',
                },
                {
                  titulo: 'Decida sobre a consulta ao Bioslab',
                  texto:
                    'Vem marcada. Com ela, o sistema já resolve as guias de cada arquivo para seus protocolos, logo depois de lê-lo. Isso não confirma nada — a revisão continua sendo sua.',
                },
                {
                  titulo: 'Envie e acompanhe',
                  texto:
                    'A tela de acompanhamento abre sozinha, com uma linha por arquivo. Os arquivos são lidos um de cada vez; clique numa linha para ver as conferências daquele arquivo.',
                },
                {
                  titulo: 'Confirme a revisão de cada arquivo',
                  texto:
                    'Na linha do arquivo, use "Abrir o arquivo" e depois "Confirmar revisão". A partir daí os valores dele alimentam a conciliação.',
                },
              ]}
            />

            <h3>Por que um de cada vez, e não todos juntos?</h3>
            <p>
              A leitura de um demonstrativo de 357 páginas leva alguns segundos e usa muito
              processamento; e a consulta ao Bioslab acontece na{' '}
              <strong>base de produção do laboratório</strong>. Oito arquivos ao mesmo tempo
              multiplicariam por oito a carga nos dois lugares. A fila é de propósito — e é o
              que permite você fechar a aba e voltar depois: o processamento continua.
            </p>

            <h3>Um arquivo deu problema. Perdi o envio?</h3>
            <p>
              Não. <strong>Cada arquivo é conferido sozinho.</strong> O que não fechar aparece
              marcado, com o motivo, e os demais seguem normalmente. Você não precisa reenviar
              os sete que deram certo por causa de um.
            </p>

            <h3>Enviei o mesmo arquivo duas vezes. E agora?</h3>
            <p>
              Nada acontece — e é de propósito. O sistema guarda uma impressão digital de cada
              arquivo recebido e recusa duplicatas, informando quando e com que nome o arquivo já
              foi enviado. Isso evita o pior erro possível aqui: <strong>baixar o mesmo valor
              duas vezes</strong>. Num envio com vários arquivos, só o repetido é recusado; os
              outros entram normalmente.
            </p>
          </Secao>

          {/* ── 05 ────────────────────────────────────────────────────── */}
          <Secao id="conferencias" num={5} titulo="As conferências aritméticas">
            <p className="ff-doc__lide">
              Este é o ponto mais importante para confiar no sistema, e vale entender o
              raciocínio.
            </p>
            <p>
              Ninguém consegue conferir 7.978 linhas no olho. Uma tabela pode parecer
              perfeitamente correta e ter centenas de registros faltando — o erro não aparece.
              Por isso o FluxFin não pede que você <em>olhe</em> os dados: ele{' '}
              <strong>prova</strong> que leu certo, usando os totais que a própria operadora
              imprimiu no documento.
            </p>

            <h3>As três conferências</h3>
            <TabelaRef
              colunas={['Conferência', 'O que compara']}
              linhas={[
                [
                  <strong>Soma das linhas = subtotal impresso</strong>,
                  'Para cada guia, soma os procedimentos extraídos e compara com o subtotal que o documento imprime ao fim daquela guia. Cobre 100% das linhas.',
                ],
                [
                  <strong>Soma dos subtotais = total do documento</strong>,
                  'Soma os subtotais de todas as guias e compara com o total geral impresso. Detecta arquivo truncado ou página perdida.',
                ],
                [
                  <strong>Toda guia tem subtotal</strong>,
                  'Garante que nenhum bloco de guia ficou sem o marcador de fechamento.',
                ],
              ]}
            />

            <Exemplo titulo="Por que isso não é exagero">
              <p>
                Durante o desenvolvimento, um detalhe de leitura fez o sistema perder{' '}
                <strong>cerca de 700 das 7.978 linhas</strong>. A tabela resultante parecia
                impecável — colunas certas, valores plausíveis, nada estranho à vista. Só a
                conferência de totais acusou. Sem ela, a baixa teria saído com quase 9% a menos.
              </p>
            </Exemplo>

            <h3>O que acontece se não fechar</h3>
            <p>
              O arquivo é marcado como <strong>reprovado</strong> e{' '}
              <strong>os dados não são gravados</strong>. O sistema prefere admitir que não sabe a
              entregar um número que parece certo. O registro do arquivo e o diagnóstico ficam
              guardados para um administrador analisar.
            </p>
          </Secao>

          {/* ── 06 ────────────────────────────────────────────────────── */}
          <Secao id="de-onde-vem" num={6} titulo="De onde vem cada número">
            <p>
              O FluxFin junta dados de duas fontes diferentes. Saber qual é qual evita a maior
              parte das dúvidas. Em várias telas há um ícone{' '}
              <i className="bi bi-question-circle" aria-hidden="true" /> ao lado do nome da
              coluna, que mostra exatamente esta informação sem sair da tela.
            </p>

            <TabelaRef
              colunas={['Campo na tela', 'Fonte', 'Campo de origem']}
              linhas={[
                [
                  <strong>Valor pago</strong>,
                  'Arquivo da operadora',
                  <code>Valor (R$)</code>,
                ],
                [
                  <strong>Cobrado (Bioslab)</strong>,
                  'Bioslab',
                  <code>Exame_atendimento.vlr_contratante</code>,
                ],
                [<strong>Guia</strong>, 'Arquivo da operadora', <code>Guia</code>],
                [
                  <strong>Protocolo</strong>,
                  'Bioslab',
                  <code>Fat_Apresentacao.NR_protocolo</code>,
                ],
                [<strong>Fatura</strong>, 'Bioslab', <code>Fatura.nr_fatura</code>],
                [
                  <strong>Atendimento</strong>,
                  'Arquivo da operadora',
                  <code>Data</code>,
                ],
                [
                  <strong>Competência</strong>,
                  'Informada no upload',
                  'mês do pagamento, conforme o cabeçalho do demonstrativo',
                ],
                [
                  <strong>Diferença</strong>,
                  'Calculado',
                  <code>valor cobrado − valor pago − valor glosado</code>,
                ],
                [<strong>Razão</strong>, 'Calculado', <code>valor pago ÷ valor cobrado</code>],
              ]}
            />

            <h3>O Bioslab é apenas consultado</h3>
            <p>
              O FluxFin <strong>nunca grava nada</strong> no Bioslab nem no Nasajon. A consulta é
              somente de leitura, e acontece <strong>uma vez por guia</strong>, no momento em que
              você resolve os protocolos. Depois disso o vínculo fica guardado aqui — navegar
              pelas telas não gera nenhuma consulta nova.
            </p>
          </Secao>

          {/* ── 07 ────────────────────────────────────────────────────── */}
          <Secao id="tela-protocolo" num={7} titulo="Lendo: Baixa por protocolo">
            <p className="ff-doc__lide">
              É a tela que responde à pergunta central: quanto lançar de baixa. Cada linha é{' '}
              <strong>um lançamento</strong> — um protocolo, num arquivo, numa competência.
            </p>

            <Exemplo titulo="Por que não é uma linha por protocolo">
              <p>
                Um protocolo recebe ao longo do tempo. Suponha que o protocolo{' '}
                <code>476.03.2026</code> tenha recebido <strong>R$ 50 mil</strong> no
                demonstrativo de fevereiro e mais <strong>R$ 10 mil</strong> no de março.
              </p>
              <p style={{ marginTop: 10 }}>
                Se a tela somasse os dois, março diria "baixar R$ 60 mil" — e os R$ 50 mil de
                fevereiro <strong>já teriam sido lançados no Nasajon</strong>. Seria baixa em
                duplicidade: dinheiro baixado duas vezes, descoberto meses depois numa
                conciliação que não fecha.
              </p>
              <p style={{ marginTop: 10 }}>
                Por isso o protocolo aparece <strong>duas vezes</strong>, uma por arquivo: em
                fevereiro você lança R$ 50 mil, em março lança R$ 10 mil.
              </p>
            </Exemplo>

            <TabelaRef
              colunas={['Coluna', 'Como ler']}
              linhas={[
                [
                  <strong>Protocolo</strong>,
                  'O código do processo no Bioslab. Quando ele já recebeu antes, aparece embaixo "3º recebimento · acum. R$ …" — o acumulado é referência, NÃO é o valor a lançar.',
                ],
                [
                  <strong>Arquivo</strong>,
                  'O demonstrativo de onde veio este dinheiro, com a operadora embaixo. É por arquivo que a baixa é lançada.',
                ],
                [
                  <strong>Competência</strong>,
                  'O mês em que a operadora pagou. Um mesmo protocolo pode aparecer em várias, uma por recebimento.',
                ],
                [<strong>Guias</strong>, 'Quantas guias daquele protocolo vieram neste arquivo.'],
                [<strong>Cobrado (Bioslab)</strong>, 'Quanto o Bioslab registra que foi cobrado nessas guias.'],
                [
                  <strong>Valor a baixar</strong>,
                  'Quanto a operadora pagou NESTE arquivo, para este protocolo. É este o número que vai para o Nasajon.',
                ],
                [
                  <strong>Glosado</strong>,
                  'O que a operadora recusou nestas guias. Célula vazia não é zero: significa que nenhum arquivo falou de glosa ainda.',
                ],
                [
                  <strong>Diferença</strong>,
                  'O que sobrou depois de descontar o pagamento E a glosa. Positiva é dinheiro que faltou; negativa é pagamento acima do cobrado. Veja a seção 10 antes de tirar conclusões.',
                ],
                [<strong>Atendimentos</strong>, 'A faixa de datas dos atendimentos que compõem o lançamento.'],
              ]}
            />

            <h3>A linha do tempo de um protocolo</h3>
            <p>
              Clique numa linha. Se aquele protocolo já recebeu antes, o topo do detalhe mostra{' '}
              <strong>todos os recebimentos dele</strong>, em ordem, com o acumulado corrido e o
              recebimento atual destacado. É o que responde "isto é a primeira parcela ou a
              terceira?".
            </p>

            <h3>Vindo da Visão geral</h3>
            <p>
              Clicar num arquivo na Visão geral traz você para cá <strong>já filtrado por ele</strong>:
              você vê exatamente o que aquele demonstrativo manda baixar. Um aviso no topo indica
              o filtro, e o botão "Ver todos os arquivos" o remove.
            </p>

            <h3>Por que o valor da baixa é o bruto</h3>
            <p>
              Um mesmo arquivo traz guias de vários protocolos, mas os impostos retidos e os
              descontos gerais existem <strong>apenas no total do documento</strong>, sem vínculo
              com nenhuma guia. Não há informação para distribuí-los entre os protocolos — então a
              baixa usa o valor bruto pago por guia, e os abatimentos são tratados à parte no
              financeiro.
            </p>

            <Exemplo titulo="A conta do demonstrativo de exemplo">
              <code className="ff-conta">
                {`Total bruto               R$ 93.000,01
(−) glosa                 R$    656,37
                          ─────────────
Pago por guia             R$ 92.343,64   ← soma das baixas
(−) auditoria técnica     R$    865,86
(−) impostos retidos      R$  8.370,22
                          ─────────────
Creditado em conta        R$ 83.107,56`}
              </code>
              <p style={{ marginTop: 12 }}>
                A soma das baixas <strong>não vai bater</strong> com o extrato bancário, e isso
                está correto. A diferença de R$ 9.236,08 são impostos e desconto, não erro de
                conciliação. Na tela de Arquivos ela aparece na coluna{' '}
                <strong>Retido/desc.</strong>
              </p>
            </Exemplo>
          </Secao>

          {/* ── 08 ────────────────────────────────────────────────────── */}
          <Secao id="tela-guias" num={8} titulo="Lendo: Por guia">
            <p>
              A mesma pergunta da <strong>Baixa por protocolo</strong>, um nível
              abaixo: em vez de "quanto lançar neste protocolo", responde{' '}
              <strong>"o que aconteceu com cada guia"</strong>. Serve para ir ao
              caso individual — conferir uma guia que o faturamento questionou,
              achar as que não resolveram protocolo, ou isolar as que sobraram
              com diferença.
            </p>

            <h3>Uma linha é uma guia dentro de um arquivo</h3>
            <p>
              Não é uma linha por guia. A mesma guia pode aparecer em mais de um
              recebimento, e juntar as ocorrências esconderia que houve dois
              pagamentos — é o mesmo motivo pelo qual a baixa é por{' '}
              <em>protocolo × arquivo</em>.
            </p>

            <h3>Os dois números de guia, lado a lado</h3>
            <p>
              Quando o laboratório usa um número diferente do que a operadora
              imprimiu, ele aparece como <strong>alt.</strong> embaixo. É por esse
              que o Bioslab é consultado, porque o Bioslab é o sistema do
              laboratório — e é isso que explica, sem ninguém precisar perguntar,
              por que uma guia resolveu e outra não.
            </p>

            <h3>A guia sem protocolo aparece</h3>
            <p>
              Com o campo vazio e a situação dizendo isso. Ela é dinheiro que
              entrou e que o sistema ainda não sabe de qual processo é; escondê-la
              faria a tela somar menos que o arquivo, sem nada explicando a
              diferença. Para resolvê-la, use a tela de{' '}
              <strong>Pendências</strong>.
            </p>

            <h3>O filtro "Situação" é o atalho do dia a dia</h3>
            <p>
              Ele recorta direto o que pede ação:{' '}
              <strong>sem protocolo</strong> (não resolveu),{' '}
              <strong>não fecha a conferência</strong> (a soma dos itens difere do
              subtotal impresso), <strong>com glosa</strong>,{' '}
              <strong>com diferença</strong> (sobrou algo depois de pagamento e
              glosa) e <strong>vínculo manual</strong> (alguém informou o protocolo
              à mão, ou ele veio pela guia alternativa).
            </p>

            <h3>Os totais são do recorte, nunca da página</h3>
            <p>
              Os quatro cartões no topo descrevem{' '}
              <strong>tudo o que o filtro selecionou</strong>, não as 50 linhas
              visíveis. Um total que encolhesse ao virar a página não seria total de
              nada. A ordenação também atravessa as páginas: clicar em "Pago" traz a
              maior guia da base para a primeira linha, não a maior da página atual.
            </p>
          </Secao>

          <Secao id="tela-arquivos" num={9} titulo="Lendo: Arquivos">
            <p>
              Lista todos os demonstrativos recebidos, com o resultado das conferências de cada
              um. Clique em qualquer linha para abrir o detalhe.
            </p>
            <ul>
              <li>
                <strong>Conferências</strong> — mostra <code>3 de 3</code> em verde quando tudo
                fechou, ou quantas falharam em vermelho.
              </li>
              <li>
                <strong>Bruto pago</strong> — a soma extraída do arquivo.
              </li>
              <li>
                <strong>Retido/desc.</strong> — impostos retidos mais descontos gerais. Não é erro
                de conciliação; é a diferença entre o bruto e o que caiu na conta.
              </li>
              <li>
                <strong>Status</strong> — em que ponto do caminho o arquivo está (seção 3).
              </li>
            </ul>
            <p>
              Use a busca para filtrar por operadora, nome do arquivo ou competência. Clique nos
              cabeçalhos para ordenar.
            </p>
          </Secao>

          {/* ── 09 ────────────────────────────────────────────────────── */}
          <Secao id="tela-pendencias" num={10} titulo="Lendo: Pendências">
            <p>
              Guias que foram extraídas de um arquivo mas <strong>ainda não chegaram a um
              protocolo</strong>. São o trabalho que sobra — e cada motivo pede uma ação
              diferente. Clique num cartão no topo para filtrar por motivo.
            </p>

            <TabelaRef
              colunas={['Motivo', 'O que aconteceu', 'O que fazer']}
              linhas={[
                [
                  <strong>Bioslab indisponível</strong>,
                  'A consulta falhou — rede, servidor fora do ar, credencial. Nada se perdeu.',
                  'Selecionar a pendência e clicar em Reprocessar. É o único motivo em que insistir resolve.',
                ],
                [
                  <strong>Guia não encontrada</strong>,
                  'A guia do arquivo não existe no Bioslab com aquele número.',
                  'Reprocessar não adianta. Conferir o número no demonstrativo e, se for exceção, resolver à mão.',
                ],
                [
                  <strong>Sem protocolo</strong>,
                  'A guia existe no Bioslab, mas ainda não foi apresentada em nenhum protocolo.',
                  'Verificar com o faturamento. A operadora pagou algo que ainda não foi apresentado.',
                ],
                [
                  <strong>Múltiplos protocolos</strong>,
                  'A guia resolveu para mais de um protocolo — normalmente refaturamento depois de glosa.',
                  'Precisa de decisão humana: o sistema não escolhe sozinho, para não baixar no lugar errado.',
                ],
              ]}
            />

            <h3>Transitório e definitivo — a diferença que muda o que fazer</h3>
            <p>
              Só <strong>Bioslab indisponível</strong> se resolve por nova tentativa: a causa é a
              conexão, e ela volta. Os outros três motivos <strong>não mudam por insistência</strong>
              — a guia não existe, não foi apresentada, ou resolveu para mais de um protocolo. A tela
              marca essas linhas com <em>"nova tentativa não resolve"</em>, para você não gastar tempo
              num botão que não vai mudar nada.
            </p>

            <h3>Reprocessar as selecionadas</h3>
            <p>
              Marque as pendências com a caixa à esquerda e clique em <strong>Reprocessar
              selecionadas</strong>. O sistema consulta o Bioslab <strong>apenas para essas guias</strong> —
              diferente de reconciliar o arquivo inteiro, que consultaria centenas de guias já
              resolvidas sem necessidade.
            </p>
            <p>
              A cada tentativa, a coluna <strong>Tentativas</strong> sobe e o motivo e a mensagem
              passam a ser os da <strong>última</strong> tentativa. Se o número sobe e a mensagem não
              muda, insistir não vai resolver aquele caso.
            </p>

            <h3>Resolver à mão</h3>
            <p>
              Para as exceções que a consulta não alcança. Você abre o Bioslab pela tela dele,
              encontra o protocolo daquela guia e informa aqui, no botão <strong>Resolver à
              mão</strong>. A pendência se fecha e o valor passa a compor a baixa do protocolo.
            </p>
            <Exemplo titulo="O que o sistema exige, e por quê">
              <p>
                <strong>A justificativa é obrigatória.</strong> Um protocolo digitado à mão entra na
                mesma soma que um vindo do Bioslab. Sem o motivo registrado, daqui a alguns meses
                existe um vínculo na baixa e ninguém sabe com base em quê.
              </p>
              <p>
                <strong>O vínculo fica marcado como manual</strong> — na tela de Baixa por protocolo,
                tanto na lista quanto no detalhe das guias. Não é desconfiança de quem preencheu: é
                para que a diferença de procedência continue visível quando ninguém mais lembrar do
                caso.
              </p>
            </Exemplo>
            <p>
              O modal mostra também <strong>o que a extração leu para aquela guia</strong>. Quando o
              arquivo traz um segundo número de guia diferente do primeiro, ele costuma ser a razão
              de o Bioslab não encontrar — e aparece ali para você conferir.
            </p>

            <p>
              Uma pendência resolvida <strong>não some manualmente</strong>: ela se fecha sozinha
              assim que a guia é resolvida, seja por nova tentativa ou pela resolução à mão.
            </p>
          </Secao>

          {/* ── 10 ────────────────────────────────────────────────────── */}
          <Secao id="tela-divergencias" num={11} titulo="Lendo: Divergências">
            <p className="ff-doc__lide">
              Mostra, lado a lado, o que o Bioslab registra como cobrado e o que a operadora
              efetivamente pagou.
            </p>

            <h3>A glosa entra na conta — e explica a maior parte</h3>
            <p>
              Até outubro de 2026 a coluna <strong>Diferença</strong> era{' '}
              <code>pago − cobrado</code>, e com isso chamava de divergência o que a
              glosa já explicava. Uma guia cobrada a R$ 1.720,05, paga a R$ 1.649,05 e
              glosada em R$ 71,00 aparecia com R$ 71,00 de divergência —{' '}
              <strong>quando não há divergência nenhuma</strong>: a operadora pagou
              uma parte, recusou a outra, e as duas somam exatamente o que foi cobrado.
            </p>
            <p>
              A conta passou a ser{' '}
              <code>valor cobrado − valor pago − valor glosado</code>. O que sobra é o
              que <strong>ninguém explicou</strong>: positivo, faltou dinheiro;
              negativo, a operadora pagou acima do cobrado.
            </p>
            <p>
              <strong>Célula vazia não é zero.</strong> Significa que não dá para
              calcular — falta o cobrado do Bioslab, ou a glosa daquela competência
              ainda não chegou. Na Unimed a glosa vem num segundo documento, e tratar
              o que não se sabe como zero criaria uma diferença do tamanho da glosa
              que falta: um problema inventado.
            </p>
            <p>
              Por isso os dois sentidos aparecem <strong>separados</strong> e nunca
              somados. No acervo atual eles quase se anulam — R$ 18,5 mil faltando
              contra R$ 18,5 mil pagos a mais —, e um número só diria que não há
              problema nenhum, quando há 1.808 guias para olhar.
            </p>

            <h3>Esta tela não acusa ninguém</h3>
            <p>
              Uma diferença aqui <strong>não significa</strong> glosa, erro ou dinheiro perdido. O
              sistema deliberadamente <strong>não classifica</strong> a diferença, porque as
              causas possíveis são várias e ainda não há histórico para distinguir: pode ser
              ajuste feito pelo faturamento antes do envio, glosa parcial, deflator contratual ou
              simplesmente tabelas de preço diferentes entre os dois sistemas.
            </p>

            <h3>A tabela "Agrupado por razão" é a mais útil</h3>
            <p>
              A <strong>razão</strong> é o valor pago dividido pelo cobrado. Um percentual que se
              repete em muitas guias quase nunca é erro — é regra. Agrupar por razão é o que faz
              esse padrão aparecer.
            </p>

            <Exemplo titulo="O que o primeiro arquivo real revelou">
              <p>
                Analisando uma única guia à mão, a razão era 93,3333% — parecia um deflator
                aplicado pela operadora. Com as 503 divergências do arquivo inteiro na tela, o
                quadro ficou outro:
              </p>
              <code className="ff-conta">
                {`109,0909%  (12/11)   242 guias   pago MAIOR que o cobrado
133,3333%  ( 4/3 )    62 guias   pago MAIOR que o cobrado
 70,0000%  ( 7/10)    17 guias
 75,0000%  ( 3/4 )    16 guias
 93,3333%  (14/15)     8 guias   ← a hipótese inicial`}
              </code>
              <p style={{ marginTop: 12 }}>
                Na maioria dos casos a operadora pagou <strong>mais</strong> do que o Bioslab
                registra — o oposto do que uma amostra pequena sugeria. E as razões são frações
                simples, o que aponta para tabelas de preço distintas, não para glosa.
              </p>
            </Exemplo>

            <h3>Razão recorrente: o alerta que mais importa</h3>
            <p>
              Clique numa razão para abri-la. Ela se desdobra em{' '}
              <strong>competência, operadora e arquivo</strong> — e quando a mesma razão aparece
              em duas competências ou mais, vem marcada como <strong>recorrente</strong>.
            </p>
            <p>
              A distinção é prática. Uma razão que aparece num mês só pode ser particularidade
              daquele demonstrativo. A mesma razão voltando em fevereiro, março e abril, na mesma
              operadora, <strong>não é ruído</strong>: é um padrão que quase sempre nasce de como
              o faturamento monta o envio. Vale corrigir na origem, em vez de recorrer guia a
              guia todo mês.
            </p>
            <p>
              Dentro do grupo aberto, "Ver as guias desta razão" filtra a lista de baixo para
              exatamente as guias que ele conta.
            </p>

            <p>
              Enquanto esse padrão não for esclarecido com a operadora, <strong>não trate a
              diferença como saldo a receber</strong>. Use a tela para juntar evidência e levar a
              conversa com números.
            </p>
          </Secao>

          {/* ── 11 ────────────────────────────────────────────────────── */}
          <Secao id="tela-glosas" num={12} titulo="Lendo: Glosas e auditoria">
            <p>
              Glosa é a recusa, total ou parcial, do pagamento de um procedimento. O
              sistema lê o extrato de glosa da operadora e guarda, por item, o valor
              recusado e <strong>todas</strong> as causas — um mesmo item pode ter mais de
              uma, e guardar só a primeira perderia a segunda em silêncio.
            </p>

            <h3>Duas telas, duas perguntas</h3>
            <p>
              <strong>Glosas</strong> é o panorama: responde <em>onde está o problema</em>.
              A mesma pergunta aparece em cinco recortes, e cada um serve a uma decisão
              diferente:
            </p>
            <ul>
              <li>
                <strong>Por causa</strong> — o que dá para corrigir na origem, antes do
                envio. É o recorte mais valioso: se uma única causa concentra a maioria
                das glosas, quase nunca é recusa caso a caso da operadora. No primeiro
                arquivo real, <strong>44 das 56 glosas</strong> eram "procedimento contido
                no principal" — erro de faturamento, corrigível antes de enviar.
              </li>
              <li>
                <strong>Por competência</strong> — se o problema está piorando ou já
                passou.
              </li>
              <li>
                <strong>Por arquivo</strong> — qual remessa concentrou o problema.
              </li>
              <li>
                <strong>Por protocolo</strong> — quanto daquele processo não entrou.
              </li>
              <li>
                <strong>Por procedimento</strong> — qual exame é recusado com frequência.
              </li>
            </ul>
            <p>
              O valor total <strong>não muda</strong> quando você troca de recorte. Ele só
              muda com o filtro de competência, que é o que você pediu explicitamente. Se
              mudasse junto, trocar o agrupamento daria a impressão de que o dinheiro
              mudou.
            </p>

            <p>
              <strong>Auditoria de glosa</strong> é a mesa de trabalho: responde{' '}
              <em>vale recorrer</em>. Cada bloco é uma guia, com o caminho completo no
              cabeçalho — arquivo › competência › protocolo › guia — e, dentro dela,{' '}
              <strong>todos</strong> os procedimentos, com <strong>Pago</strong> e{' '}
              <strong>Glosado</strong> lado a lado.
            </p>

            <h3>Por que a guia aparece inteira, e não só o que foi glosado</h3>
            <p>
              Porque é a <strong>proporção</strong> que decide. Um item recusado em R$ 4,50
              numa guia que recebeu R$ 144,00 é uma coisa; o mesmo R$ 4,50 numa guia que
              recebeu R$ 4,50 é outra. Ver só o que foi recusado responderia "quanto
              glosaram"; ver a guia inteira responde "vale recorrer". O percentual no
              cabeçalho de cada guia é esse número.
            </p>

            <h3>A coluna Glosado vazia não é zero</h3>
            <p>
              Quando o procedimento não teve glosa, a célula fica <strong>vazia</strong>,
              não "R$ 0,00". A diferença importa: "R$ 0,00" se lê como "foi analisado e
              nada foi recusado", que é uma afirmação; vazio diz apenas que não houve
              glosa ali.
            </p>

            <h3>Glosa parcial</h3>
            <p>
              Um mesmo procedimento pode ter parte paga e parte recusada. No arquivo real
              existe um caso assim: pago R$ 2,00 e glosado R$ 2,00, com a causa{' '}
              <span className="ff-mono">9631 — VALOR A PAGAR INFERIOR AO VALOR COBRADO</span>
              . As duas colunas mostram isso na mesma linha.
            </p>

            <h3>Glosa sem protocolo</h3>
            <p>
              Se a guia ainda não foi conciliada com o Bioslab, a glosa dela aparece
              assim mesmo, marcada como <strong>sem protocolo</strong>. Escondê-la seria
              perder de vista dinheiro recusado só porque a conciliação ficou pendente —
              e é justamente o caso que merece atenção.
            </p>

            <p>
              Os filtros ficam na barra de endereço. Isso é de propósito: você pode salvar
              o recorte nos favoritos, voltar nele depois e mandar o link para outra
              pessoa, que vai ver exatamente a mesma tela.
            </p>
          </Secao>

          <Secao id="tela-saldo" num={13} titulo="Lendo: Saldo a receber">
            <p>
              Esta tela responde a pergunta que nenhuma outra respondia:{' '}
              <strong>quanto ainda falta receber</strong>. As demais olham para o que
              chegou; esta olha para o que foi faturado e compara.
            </p>

            <Exemplo titulo="A conta, e só ela">
              <p className="ff-mono">
                total cobrado &minus; recebido &minus; glosado = saldo a receber
              </p>
            </Exemplo>

            <p>
              O <strong>total cobrado</strong> é o tamanho do bolo: tudo o que o
              laboratório faturou naquele protocolo, somando todas as guias dele —
              inclusive as que ainda não apareceram em demonstrativo nenhum. Ele vem do
              Bioslab e não existe em lugar nenhum dos arquivos da operadora. O{' '}
              <strong>recebido</strong> e o <strong>glosado</strong> o sistema já
              apurava dos demonstrativos. O que sobra é dinheiro que a operadora ainda
              não pagou e também não recusou.
            </p>

            <h3>O botão "Atualizar totais"</h3>
            <p>
              O total cobrado só aparece depois de consultar o Bioslab, e essa consulta
              precisa ser disparada: clique em <strong>Atualizar totais</strong>. O botão
              aparece para <strong>gestor e administrador</strong> — a consulta gera carga
              no sistema do laboratório, e quem a aciona é quem responde por ele. Quem é
              operador continua vendo o saldo inteiro, só não dispara a atualização. A consulta vai{' '}
              <strong>apenas aos protocolos que já apareceram em algum arquivo
              lançado</strong> — nunca à base inteira do laboratório. Isso é uma regra do
              sistema, não uma economia: o FluxFin só precisa saber dos protocolos que
              ele próprio conciliou.
            </p>
            <p>
              Vale reconsultar de tempos em tempos. O vínculo guia → protocolo não muda
              depois de faturado, mas o total cobrado, sim: guias novas podem entrar no
              mesmo protocolo. O rodapé da tela diz a data da última consulta.
            </p>

            <h3>O que ainda não foi revisado não conta</h3>
            <p>
              Um arquivo que fecha as conferências aritméticas fica{' '}
              <strong>aguardando a revisão humana</strong> — e, até você confirmar essa
              revisão, o dinheiro dele <strong>não</strong> entra como recebido aqui. É a
              mesma regra da Baixa por protocolo, e é a regra que o laboratório fixou no
              começo do projeto: dado extraído só vira oficial depois que alguém confere.
            </p>
            <p>
              Mas esse valor também não some. Ele aparece na coluna{' '}
              <strong>Em revisão</strong> e num aviso no topo da tela. Se sumisse, o saldo
              cairia sozinho no dia em que alguém confirmasse a revisão, sem nada ligando
              uma coisa à outra — e a primeira reação seria achar que o sistema errou.
            </p>

            <h3>As quatro situações</h3>
            <TabelaRef
              colunas={['Situação', 'O que significa', 'O que fazer']}
              linhas={[
                [
                  'A receber',
                  'Foi faturado, não foi pago e não foi recusado.',
                  'É dinheiro a cobrar da operadora. A lista vem ordenada pelo maior saldo, que é por onde se começa.',
                ],
                [
                  'Quitado',
                  'O que entrou mais o que foi glosado fecha com o cobrado.',
                  'Nada. Diferenças de até cinco centavos são arredondamento, não saldo.',
                ],
                [
                  'Inconsistente',
                  'A operadora pagou MAIS do que o Bioslab registra como cobrado.',
                  'Não é saldo negativo. É a divergência de tabela de preço — veja a tela de Divergências.',
                ],
                [
                  'Sem total',
                  'O Bioslab ainda não foi consultado para esse protocolo.',
                  'Clique em "Atualizar totais". Sem total, o saldo não é zero: é desconhecido.',
                ],
              ]}
            />

            <p>
              <strong>A distinção entre "quitado" e "sem total" é a mais importante da
              tela.</strong> As duas aparecem sem nada a receber, mas por motivos
              opostos: uma porque a conta fechou, a outra porque a conta não foi feita.
              Tratá-las como a mesma coisa faria o painel afirmar que não há nada a
              receber quando o que há é falta de informação. Por isso, quando existe
              algum protocolo sem total, a tela avisa logo acima dos números.
            </p>

            <h3>Por que "inconsistente" não entra no total a receber</h3>
            <p>
              Quando a operadora paga mais do que o Bioslab registra, a conta dá
              negativo. Se esse valor fosse somado junto, ele abateria uma divergência
              contra uma cobrança legítima — e o total diria menos do que há de fato para
              cobrar. O sistema soma no <strong>Ainda a receber</strong> apenas o saldo de
              quem está na situação "a receber"; os inconsistentes são contados à parte,
              no rodapé do mesmo indicador.
            </p>

            <h3>"Parado há" — a coluna que separa normal de atrasado</h3>
            <p>
              Mostra há quantos meses aquele protocolo não recebe nada. Um saldo de três
              meses é fluxo normal; o mesmo saldo parado há oito é outra conversa, e a
              lista ordenada só por valor não distinguiria os dois. A partir de seis meses
              o número vem destacado.
            </p>
            <p>
              Vem da <strong>última competência em que o protocolo recebeu</strong>, não
              do número do protocolo. O número <span className="ff-mono">476.03.2026</span>{' '}
              parece <em>sequencial.mês.ano</em>, mas isso nunca foi confirmado com a
              operadora — e errar o mês mudaria a leitura de "normal" para "atrasado".
            </p>

            <h3>O saldo ao longo do tempo</h3>
            <p>
              A partir da segunda atualização de totais, aparece um painel com o histórico:
              quanto havia a receber em cada apuração, e quanto subiu ou caiu desde a
              anterior. O valor de hoje diz quanto falta; <strong>a sequência diz se a
              cobrança está funcionando</strong>.
            </p>
            <p>
              Cada atualização tira uma foto, e ela não dá para reconstruir depois: o total
              cobrado muda quando guias novas entram no protocolo, e o documento original é
              apagado pela política de retenção em 180 dias. Por isso a foto é tirada na
              hora, mesmo que ninguém vá olhar logo.
            </p>

            <h3>A barra de composição</h3>
            <p>
              Cada linha traz uma barra fina com três faixas: verde é o que entrou,
              vermelho é o que foi glosado, e o espaço vazio à direita é o que falta. É a
              proporção que informa — ver que metade da barra está vazia diz mais rápido
              do que ler três valores.
            </p>

            <h3>Recebido em</h3>
            <p>
              A coluna mostra em quais competências aquele protocolo recebeu, e quanto em
              cada uma. Um protocolo costuma receber em mais de uma: o pagamento parcial
              é a regra, não a exceção. Sem essa coluna o saldo diria o quanto falta sem
              dizer o que já aconteceu.
            </p>

            <h3>"Total suspeito"</h3>
            <p>
              Quando aparece um ponto de interrogação ao lado do protocolo, o mesmo{' '}
              <strong>documento de cobrança pertence a mais de um protocolo</strong> — então
              os exames dele contariam nos dois, e escolher qual é o certo não é tarefa do
              sistema. O número continua gravado, mas marcado: exibir um valor duvidoso
              como fato é pior do que não ter o valor.
            </p>

            <h3>Os dois recortes acima da lista</h3>
            <p>
              <strong>Por operadora</strong> responde "quanto a Unimed ainda me deve",
              que a lista protocolo a protocolo responderia devagar.{' '}
              <strong>Entradas por competência</strong> responde "quando o dinheiro
              chegou" — e, lado a lado com o saldo, mostra se o ritmo de entrada está
              acompanhando o que foi faturado.
            </p>

            <p>
              Os filtros funcionam em cascata, como no Excel: escolher uma competência
              reduz a lista de operadoras às que de fato aparecem nela. Cada filtro
              continua oferecendo as próprias alternativas, para que dê para trocar sem
              antes limpar.
            </p>
          </Secao>

          <Secao id="tela-agenda" num={14} titulo="Lendo: Agenda de envios">
            <p>
              É a primeira tela do sistema orientada ao que <strong>não</strong> chegou.
              As outras mostram o que foi lançado; esta mostra o que está faltando.
            </p>

            <p>
              Algumas operadoras mandam mais de um arquivo por competência, e eles se
              completam. A Unimed manda o <strong>demonstrativo de pagamento</strong> e o{' '}
              <strong>extrato de glosa</strong>: o primeiro diz quanto entrou, o segundo
              diz o que foi recusado e por quê. Quando só um dos dois chega, o mês fica
              contado pela metade — e nada no sistema reclamaria disso sozinho.
            </p>

            <Exemplo titulo="O que a agenda alerta">
              <p>
                Chegou o <span className="ff-mono">1547.pdf</span> (recebimento) de
                05/2026, mas não chegou o{' '}
                <span className="ff-mono">R_GLOSA_1547.pdf</span>. A competência aparece
                marcada como <strong>incompleta</strong>, com o papel que falta nomeado.
              </p>
            </Exemplo>

            <p>
              Numa mesma competência a Unimed pode mandar oito arquivos de recebimento e
              oito de glosa. A agenda conta os dois lados e compara: o que importa é que
              cada papel esperado tenha chegado, não quantos arquivos vieram.
            </p>

            <h3>Os valores ficam separados por papel</h3>
            <p>
              Recebimento e glosa <strong>nunca são somados</strong>. Um é dinheiro que
              entrou; o outro é dinheiro que foi recusado. Somá-los produziria um número
              que não significa nada — e foi exatamente o que a versão anterior do cartão
              na Visão geral fazia. Hoje cada valor aparece do lado do seu arquivo, com o
              nome do que é.
            </p>

            <h3>Meses em que nada chegou ficam quietos</h3>
            <p>
              A agenda só reclama de <strong>par incompleto</strong>. Ela não sabe (ainda)
              o que cada operadora deveria mandar e de quanto em quanto tempo, então um
              mês sem nenhum arquivo não gera alerta — seria um alarme sem fundamento.
              Quando houver um cadastro de "envio esperado", isso muda.
            </p>

            <h3>Glosa que chega antes do pagamento</h3>
            <p>
              É aceita e fica guardada. Ela não gera baixa — não traz dinheiro novo,
              explica um abatimento — e enriquece as guias quando o demonstrativo de
              pagamento correspondente chegar. Até lá, aparece na agenda como par
              incompleto, sem prazo para sumir.
            </p>
          </Secao>

          <Secao id="modelos" num={15} titulo="Modelos de arquivo">
            <p>
              Cada operadora manda o demonstrativo num formato próprio — PDF, XML, CSV, planilha.
              Um <strong>modelo de leitura</strong> descreve como ler o arquivo daquela operadora.
              É cadastro, não programação: acrescentar uma operadora não exige nova versão do
              sistema.
            </p>

            <h3>Por que uma operadora não aparece na lista de upload</h3>
            <p>
              Porque ela ainda não tem modelo <strong>aprovado</strong>. Um modelo só é liberado
              depois que um administrador o aprova, e a aprovação exige um arquivo de exemplo que
              feche as conferências aritméticas.
            </p>

            <h3>A marca "Não verificado"</h3>
            <p>
              Alguns arquivos não trazem totais que permitam conferir a leitura. Nesses casos o
              administrador pode aprovar o modelo mesmo assim, e ele fica{' '}
              <strong>permanentemente marcado como não verificado aritmeticamente</strong>. Essa
              marca acompanha todo dado extraído por aquele modelo — e é um aviso de que aqueles
              números merecem conferência manual mais cuidadosa antes de virar baixa.
            </p>

            <h3>Cadastrando uma operadora nova</h3>
            <p>
              Em <strong>Modelos de arquivo → Cadastrar operadora</strong>, você sobe um
              demonstrativo de exemplo e uma IA descobre como aquele arquivo se lê. Ela{' '}
              <strong>não extrai os dados</strong>: produz a descrição de como lê-los, e quem
              extrai é sempre o mesmo motor determinístico usado no dia a dia. Por isso o mesmo
              arquivo produz sempre o mesmo número, e dá para mostrar por quê.
            </p>
            <Passos
              itens={[
                {
                  titulo: 'Suba um exemplo completo',
                  texto:
                    'Um demonstrativo real e fechado. É dos totais impressos nele que sai a prova de que a leitura está certa — um arquivo cortado não prova nada.',
                },
                {
                  titulo: 'A IA propõe o mapeamento',
                  texto:
                    'Leva de 30 segundos a 2 minutos. Ela recebe as armadilhas que este sistema já catalogou, para não repetir erros que já custaram caro aqui.',
                },
                {
                  titulo: 'O sistema põe o mapeamento à prova',
                  texto:
                    'O motor aplica a descrição proposta e tenta fechar com os totais do documento. Você vê a tabulação sempre acompanhada desse resultado, nunca sozinha.',
                },
                {
                  titulo: 'Você confere e envia ao administrador',
                  texto:
                    'Se não fechou, dá para tentar de novo (há um limite de tentativas) ou enviar assim mesmo — o administrador verá o diagnóstico completo e decidirá.',
                },
                {
                  titulo: 'O administrador aprova',
                  texto:
                    'Só então a operadora passa a aparecer na tela de lançamento. Ele decide sobre o mesmo arquivo de exemplo, sem precisar que você reenvie nada.',
                },
              ]}
            />

            <h3>Quando a operadora manda mais de um arquivo</h3>
            <p>
              É o caso da Unimed: ela manda o <strong>demonstrativo de pagamento</strong> e o{' '}
              <strong>extrato de glosa</strong>. Suba os dois de uma vez, na mesma tela. O
              sistema mapeia cada um e classifica a relação entre eles — e essa classificação
              muda o que ele faz depois:
            </p>
            <ul>
              <li>
                <strong>Complementares</strong> — cada arquivo traz uma informação diferente da
                mesma competência, como pagamento e glosa. Os dois são esperados, e falta de um
                aparece como pendência.
              </li>
              <li>
                <strong>Equivalentes</strong> — o mesmo conteúdo em formatos diferentes, como a
                planilha e o XML da GEAP. Aqui o sistema <strong>aceita o primeiro que chegar e
                recusa o outro</strong>: aceitar os dois contaria cada guia duas vezes e dobraria
                a baixa.
              </li>
            </ul>
            <p>
              A classificação é feita de duas maneiras ao mesmo tempo: pelo que os documentos
              dizem de si mesmos e pela <strong>comparação das guias que cada um traz</strong>.
              Quando as duas discordam, vale a leitura mais restritiva, e a tela pede sua
              confirmação. O motivo é assimétrico: tratar equivalentes como complementares
              dobraria uma baixa em silêncio; o contrário só gera um aviso a mais.
            </p>

            <h3>Por que existe um limite de tentativas</h3>
            <p>
              Porque um mapeamento pode simplesmente <strong>não existir</strong> para aquele
              arquivo — um PDF escaneado, por exemplo, não tem estrutura para ancorar. Sem
              limite, o sistema ficaria tentando indefinidamente. Atingido o teto, um
              administrador ajusta a descrição à mão ou se pede outro exemplo.
            </p>

            <h3>Um modelo cadastrado aqui vale para sempre?</h3>
            <p>
              Depende de onde ele foi cadastrado. Um modelo criado pela tela vive{' '}
              <strong>no banco daquele ambiente</strong>. Se foi cadastrado em produção, fica
              em produção e funciona normalmente. Se foi cadastrado num ambiente de teste,
              não acompanha a publicação.
            </p>
            <p>
              Para que um modelo passe a acompanhar todas as atualizações do sistema, um
              administrador o exporta para o repositório do projeto. A partir daí ele é
              recriado automaticamente a cada nova versão — inclusive se o banco precisar ser
              refeito.
            </p>
            <p>
              A exportação só aceita modelo <strong>aprovado e verificado
              aritmeticamente</strong>. O motivo é que um modelo exportado volta valendo, sem
              passar de novo pela aprovação: deixar sair um mapeamento sem prova seria abrir
              caminho para um número errado entrar direto em produção.
            </p>

            <h3>O documento não é enviado à IA</h3>
            <p>
              Os demonstrativos trazem <strong>nome de paciente, carteirinha e procedimento
              médico</strong> — dado de saúde. Para descobrir o layout, porém, basta a{' '}
              <strong>estrutura</strong>: saber que existe um número de 15 dígitos numa certa
              posição, não que ele vale tal coisa e pertence a fulano.
            </p>
            <p>
              O que sai do sistema é só isso: coordenadas, tipos de campo e os rótulos do
              relatório, com os valores mascarados — dígitos viram <code>0</code> e letras viram{' '}
              <code>X</code>. O arquivo em si fica neste servidor. Um administrador pode ver, na
              tela de aprovação, exatamente o conteúdo que foi enviado.
            </p>
            <Exemplo titulo="Medido na amostra da Unimed">
              <p>
                O PDF tem 1,9&nbsp;MB, 357 páginas e 1.119 nomes e termos distintos nos campos de
                pessoa. O que foi enviado à IA: <strong>10,9&nbsp;KB</strong>, com{' '}
                <strong>zero nomes</strong> e <strong>zero carteirinhas</strong> — e com os sete
                totais do documento preservados, porque são eles que provam a leitura.
              </p>
            </Exemplo>
          </Secao>

          {/* ── 12 ────────────────────────────────────────────────────── */}
          <Secao id="problemas" num={16} titulo="Quando algo não bate">
            <h3>O arquivo foi reprovado</h3>
            <p>
              Significa que os totais extraídos não bateram com os do próprio documento. Os dados{' '}
              <strong>não foram gravados</strong>. Normalmente a causa é uma destas:
            </p>
            <ul>
              <li>
                <strong>A operadora mudou o layout do arquivo.</strong> A mais comum. O modelo
                precisa de uma versão nova.
              </li>
              <li>
                <strong>Operadora errada na hora do upload.</strong> O sistema costuma barrar
                antes, avisando que o arquivo não corresponde ao modelo.
              </li>
              <li>
                <strong>Arquivo incompleto ou truncado</strong> — download interrompido, por
                exemplo.
              </li>
            </ul>
            <p>
              Em todos os casos: avise um administrador e informe o nome do arquivo. O diagnóstico
              completo fica guardado na tela do arquivo.
            </p>

            <h3>O total por protocolo não bate com o total do arquivo</h3>
            <p>
              Confira as <strong>Pendências</strong>. Toda guia que não resolveu para um protocolo
              fica fora da soma por protocolo. A diferença entre os dois números é exatamente o
              valor das guias pendentes.
            </p>

            <h3>A soma das baixas não bate com o extrato bancário</h3>
            <p>
              Está correto. O extrato traz o valor líquido, já com impostos retidos e descontos; a
              baixa usa o bruto. Veja a conta completa na seção 7.
            </p>

            <h3>Uma guia que eu esperava não está no arquivo</h3>
            <p>
              O FluxFin só conhece o que veio no demonstrativo. Se a operadora não pagou aquela
              guia, ela não aparece — e isso é informação: a guia segue em aberto.
            </p>
          </Secao>

          {/* ── 13 ────────────────────────────────────────────────────── */}
          <Secao id="limites" num={17} titulo="O que o sistema não faz">
            <p>
              Tão importante quanto saber o que a ferramenta faz é saber onde ela termina — para
              não esperar dela o que ainda é trabalho manual.
            </p>
            <ul>
              <li>
                <strong>Não lança a baixa no Nasajon.</strong> Ele calcula quanto baixar em cada
                protocolo; o lançamento continua sendo feito por você, no Nasajon.
              </li>
              <li>
                <strong>Não altera nada no Bioslab.</strong> A consulta é somente leitura.
              </li>
              <li>
                <strong>Não calcula saldo a receber.</strong> Enquanto a origem das divergências
                não estiver esclarecida, qualquer saldo calculado seria chute.
              </li>
              <li>
                <strong>Não recorre da glosa por você.</strong> O sistema lê o extrato de glosa,
                guarda o valor recusado e todas as causas de cada item — inclusive quando um item
                tem mais de uma —, mas o recurso junto à operadora continua sendo feito fora
                daqui.
              </li>
              <li>
                <strong>Não distribui impostos por protocolo.</strong> Eles existem só no total do
                arquivo, sem vínculo com guia.
              </li>
            </ul>
          </Secao>

          {/* ── 14 ────────────────────────────────────────────────────── */}
          <Secao id="perguntas" num={18} titulo="Perguntas frequentes">
            <h3>Quem vê o quê?</h3>
            <p>
              O <strong>operador</strong> usa o sistema inteiro: lança, revisa, consulta
              todas as telas. O <strong>gestor</strong> faz tudo isso, mais gerenciar os
              operadores e disparar a atualização de totais do saldo. O{' '}
              <strong>administrador</strong> faz tudo, aprova modelos de operadora e vê a
              trilha completa de auditoria.
            </p>
            <p>
              Desde outubro de 2026 o gestor também tem a tela{' '}
              <strong>Minhas ações</strong>, com o histórico do que ele próprio fez. Criar
              conta, inativar alguém e resetar senha estão entre as operações mais
              sensíveis do sistema, e antes quem as praticava não tinha como revisá-las
              nem comprová-las. A trilha completa, com as ações de todos, segue sendo do
              administrador.
            </p>

            <h3>Posso enviar o mesmo arquivo de novo para corrigir algo?</h3>
            <p>
              Não é necessário. O sistema recusa arquivos duplicados. Se precisar reprocessar, use{' '}
              <strong>Resolver protocolos</strong> de novo — a operação pode ser repetida à
              vontade, sem duplicar valores.
            </p>

            <h3>Resolver protocolos duas vezes duplica alguma coisa?</h3>
            <p>
              Não. As divergências são recalculadas do zero a cada execução, e guias já resolvidas
              não voltam a ser consultadas no Bioslab.
            </p>

            <h3>Por que a tela demora quando envio um arquivo?</h3>
            <p>
              Porque a extração acontece na hora. Um demonstrativo de 357 páginas gera quase oito
              mil linhas, todas conferidas antes de qualquer número aparecer.
            </p>

            <h3>Posso confiar no número mesmo sem conferir o PDF?</h3>
            <p>
              Se as três conferências estão verdes, sim — elas comparam a extração com os totais
              que a própria operadora imprimiu. É uma verificação mais forte que a leitura humana,
              que não conseguiria cobrir todas as linhas.
            </p>

            <h3>O que significa a diferença negativa num protocolo?</h3>
            <p>
              Que a operadora pagou <strong>mais</strong> do que o Bioslab registra como
              cobrado, mesmo depois de descontada a glosa. A diferença é{' '}
              <code>cobrado − pago − glosado</code>: positiva é dinheiro que faltou,
              negativa é pagamento a maior. Não conclua que é erro — leia a seção 10
              antes.
            </p>

            <h3>Quem pode aprovar modelos de operadora?</h3>
            <p>
              Apenas o perfil administrador. <strong>Cadastrar</strong> uma operadora nova,
              porém, qualquer usuário pode: operador e gestor sobem o arquivo de exemplo,
              conferem a tabulação e enviam para aprovação. A liberação para uso é que fica com
              o administrador.
            </p>

            <h3>A IA pode errar o mapeamento e gerar uma baixa errada?</h3>
            <p>
              Um mapeamento errado não chega a gerar baixa, porque não passa pela conferência: a
              soma das linhas não fecharia com os totais impressos no documento. É essa
              conferência, e não a aparência da tabulação, que libera um modelo. A exceção é o
              modelo aprovado sem validação aritmética — e ele fica marcado como{' '}
              <strong>não verificado</strong>, justamente para que ninguém confie nele sem olhar.
            </p>
          </Secao>
        </div>
      </div>
    </>
  );
}
